// File: frontend/src/components/ApiKeys/ApiKeysSection.tsx
// Purpose: UI component for managing API keys in user profile

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Alert,
    Chip,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Snackbar,
    CircularProgress,
    Collapse,
    InputAdornment
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    ContentCopy as CopyIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Key as KeyIcon,
    Warning as WarningIcon
} from '@mui/icons-material';
import { useApi } from '../../hooks/useApi';
import { cyber } from '../../theme';

// Types
interface ApiKeyInfo {
    key_id: string;
    key_prefix: string;
    name: string;
    active: boolean;
    created_at: string;
    expires_at: string | null;
    last_used_at: string | null;
}

interface ApiKeyListResponse {
    keys: ApiKeyInfo[];
    max_keys: number;
}

interface ApiKeyCreatedResponse {
    api_key: string;
    key_prefix: string;
    name: string;
    created_at: string;
    expires_at: string | null;
    message: string;
}

const ApiKeysSection: React.FC = () => {
    const { authenticatedFetcher, poster } = useApi();
    
    // State
    const [keys, setKeys] = useState<ApiKeyInfo[]>([]);
    const [maxKeys, setMaxKeys] = useState(5);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Dialog state
    const [createDialogOpen, setCreateDialogOpen] = useState(false);
    const [newKeyName, setNewKeyName] = useState('');
    const [creating, setCreating] = useState(false);
    
    // Newly created key display
    const [newlyCreatedKey, setNewlyCreatedKey] = useState<string | null>(null);
    const [showKey, setShowKey] = useState(false);
    
    // Confirm dialog for revoke
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [keyToRevoke, setKeyToRevoke] = useState<ApiKeyInfo | null>(null);
    const [revoking, setRevoking] = useState(false);
    
    // Snackbar
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

    // Load keys
    const loadKeys = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response: ApiKeyListResponse = await authenticatedFetcher('/api/api-keys');
            setKeys(response.keys);
            setMaxKeys(response.max_keys);
        } catch (err) {
            console.error('Failed to load API keys:', err);
            setError(err instanceof Error ? err.message : 'Failed to load API keys');
        } finally {
            setLoading(false);
        }
    }, [authenticatedFetcher]);

    useEffect(() => {
        loadKeys();
    }, [loadKeys]);

    // Create new key
    const handleCreate = async () => {
        if (!newKeyName.trim()) return;
        
        setCreating(true);
        try {
            const response: ApiKeyCreatedResponse = await poster('/api/api-keys', { 
                name: newKeyName.trim(),
                expires_in_days: 365
            });
            
            setNewlyCreatedKey(response.api_key);
            setCreateDialogOpen(false);
            setNewKeyName('');
            setShowKey(true);
            loadKeys();
            
            setSnackbar({ open: true, message: 'API key created successfully!', severity: 'success' });
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err instanceof Error ? err.message : 'Failed to create API key', 
                severity: 'error' 
            });
        } finally {
            setCreating(false);
        }
    };

    // Revoke key
    const handleRevoke = async () => {
        if (!keyToRevoke) return;
        
        setRevoking(true);
        try {
            await poster(`/api/api-keys/${keyToRevoke.key_id}`, {}, 'DELETE');
            
            setRevokeDialogOpen(false);
            setKeyToRevoke(null);
            loadKeys();
            
            setSnackbar({ open: true, message: 'API key revoked successfully', severity: 'success' });
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err instanceof Error ? err.message : 'Failed to revoke API key', 
                severity: 'error' 
            });
        } finally {
            setRevoking(false);
        }
    };

    // Regenerate key
    const handleRegenerate = async (keyId: string) => {
        try {
            const response: ApiKeyCreatedResponse = await poster(`/api/api-keys/${keyId}/regenerate`, {});
            
            setNewlyCreatedKey(response.api_key);
            setShowKey(true);
            loadKeys();
            
            setSnackbar({ open: true, message: 'API key regenerated successfully!', severity: 'success' });
        } catch (err) {
            setSnackbar({ 
                open: true, 
                message: err instanceof Error ? err.message : 'Failed to regenerate API key', 
                severity: 'error' 
            });
        }
    };

    // Copy to clipboard
    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        setSnackbar({ open: true, message: 'Copied to clipboard!', severity: 'success' });
    };

    // Format date
    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return 'Never';
        try {
            return new Date(dateStr).toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return 'Invalid date';
        }
    };

    // Check if key is expired
    const isExpired = (expiresAt: string | null) => {
        if (!expiresAt) return false;
        try {
            return new Date(expiresAt) < new Date();
        } catch {
            return false;
        }
    };

    const activeKeysCount = keys.filter(k => k.active).length;

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 3, mt: 2 }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <KeyIcon /> MCP Server API Keys
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 600 }}>
                        Generate API keys to authenticate with the TI Mindmap HUB MCP Server. 
                        Use these keys to integrate threat intelligence into AI assistants.
                        See the{' '}
                        <a href="/mcp-integration" style={{ color: 'inherit' }}>
                            MCP Integration Guide
                        </a>
                        {' '}for setup instructions.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setCreateDialogOpen(true)}
                    disabled={activeKeysCount >= maxKeys}
                >
                    Generate Key
                </Button>
            </Box>

            {/* Error */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Limit warning */}
            {activeKeysCount >= maxKeys && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    Maximum {maxKeys} active API keys reached. Revoke an existing key to create a new one.
                </Alert>
            )}

            {/* Newly Created Key Alert */}
            <Collapse in={!!newlyCreatedKey}>
                <Paper 
                    elevation={0}
                    sx={{ 
                        mb: 2,
                        p: 2.5,
                        border: '2px solid',
                        borderColor: 'success.main',
                        borderRadius: 2,
                        bgcolor: 'success.light',
                    }}
                >
                    <Typography 
                        variant="subtitle1" 
                        gutterBottom 
                        fontWeight="bold"
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            color: 'success.dark'
                        }}
                    >
                        <KeyIcon /> New API Key Created — Save it now!
                    </Typography>
                    
                    <Paper
                        variant="outlined"
                        sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1, 
                            mt: 1.5,
                            p: 1.5,
                            bgcolor: 'background.paper',
                            borderRadius: 1
                        }}
                    >
                        <Typography 
                            variant="body2" 
                            component="code" 
                            sx={{
                                fontFamily: cyber.fontMono,
                                wordBreak: 'break-all',
                                flex: 1,
                                fontSize: '0.9rem',
                                color: 'text.primary'
                            }}
                        >
                            {showKey ? newlyCreatedKey : '•'.repeat(50)}
                        </Typography>
                        <Tooltip title={showKey ? "Hide key" : "Show key"}>
                            <IconButton size="small" onClick={() => setShowKey(!showKey)}>
                                {showKey ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy to clipboard">
                            <IconButton size="small" onClick={() => newlyCreatedKey && copyToClipboard(newlyCreatedKey)}>
                                <CopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Paper>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1.5 }}>
                        <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 500 }}>
                            ⚠️ This key will only be shown once. Store it securely!
                        </Typography>
                        <Button 
                            size="small" 
                            onClick={() => setNewlyCreatedKey(null)}
                            sx={{ color: 'text.secondary' }}
                        >
                            Dismiss
                        </Button>
                    </Box>
                </Paper>
            </Collapse>

            {/* Keys Table */}
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Name</strong></TableCell>
                            <TableCell><strong>Key</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell><strong>Created</strong></TableCell>
                            <TableCell><strong>Expires</strong></TableCell>
                            <TableCell><strong>Last Used</strong></TableCell>
                            <TableCell align="right"><strong>Actions</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {keys.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <Typography color="text.secondary">
                                        No API keys yet. Generate one to start using the MCP Server.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            keys.map((key) => (
                                <TableRow key={key.key_id} sx={{ opacity: key.active ? 1 : 0.5 }}>
                                    <TableCell>{key.name}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2" component="code" sx={{ fontFamily: cyber.fontMono, color: cyber.accent.cyan }}>
                                            {key.key_prefix}...
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {!key.active ? (
                                            <Chip label="Revoked" size="small" color="default" />
                                        ) : isExpired(key.expires_at) ? (
                                            <Chip label="Expired" size="small" color="error" />
                                        ) : (
                                            <Chip label="Active" size="small" color="success" />
                                        )}
                                    </TableCell>
                                    <TableCell>{formatDate(key.created_at)}</TableCell>
                                    <TableCell>{formatDate(key.expires_at)}</TableCell>
                                    <TableCell>{formatDate(key.last_used_at)}</TableCell>
                                    <TableCell align="right">
                                        {key.active && (
                                            <>
                                                <Tooltip title="Regenerate key">
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleRegenerate(key.key_id)}
                                                    >
                                                        <RefreshIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title="Revoke key">
                                                    <IconButton 
                                                        size="small" 
                                                        color="error"
                                                        onClick={() => {
                                                            setKeyToRevoke(key);
                                                            setRevokeDialogOpen(true);
                                                        }}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Usage hint */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                📚 Need help? See the{' '}
                <a href="/mcp-integration" style={{ color: 'inherit' }}>
                    MCP Integration Guide
                </a>
                {' '}for detailed setup instructions.
            </Typography>

            {/* Create Dialog */}
            <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Generate New API Key</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        API keys allow external tools to access TI Mindmap HUB on your behalf.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Key Name"
                        placeholder="e.g., Claude Desktop, VS Code, My Script"
                        value={newKeyName}
                        onChange={(e) => setNewKeyName(e.target.value)}
                        helperText="Give your key a descriptive name to remember its purpose"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <KeyIcon color="action" />
                                </InputAdornment>
                            )
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        onClick={handleCreate}
                        disabled={!newKeyName.trim() || creating}
                    >
                        {creating ? <CircularProgress size={24} /> : 'Generate'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke Confirmation Dialog */}
            <Dialog open={revokeDialogOpen} onClose={() => setRevokeDialogOpen(false)}>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningIcon color="warning" /> Revoke API Key
                </DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to revoke <strong>{keyToRevoke?.name}</strong>?
                    </Typography>
                    <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                        This action cannot be undone. Any applications using this key will stop working.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeDialogOpen(false)}>Cancel</Button>
                    <Button 
                        variant="contained" 
                        color="error"
                        onClick={handleRevoke}
                        disabled={revoking}
                    >
                        {revoking ? <CircularProgress size={24} /> : 'Revoke Key'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    variant="filled"
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ApiKeysSection;